import React, { useState } from 'react';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import Select from '../../components/ui/Select';
import StatCard from '../role-based-dashboard/components/StatCard';
import HiringTrendsTab from './components/HiringTrendsTab';
import PlacementStatisticsTab from './components/PlacementStatisticsTab';
import EmployerPerformanceTab from './components/EmployerPerformanceTab';
import CustomReportsTab from './components/CustomReportsTab';

const AnalyticsReportingDashboard = () => {
  const [activeTab, setActiveTab] = useState('overview');
  const [dateRange, setDateRange] = useState('last-6-months');
  const [selectedRole, setSelectedRole] = useState('all');

  const dateRangeOptions = [
    { value: 'last-30-days', label: 'Last 30 Days' },
    { value: 'last-3-months', label: 'Last 3 Months' },
    { value: 'last-6-months', label: 'Last 6 Months' },
    { value: 'last-year', label: 'Last Year' },
    { value: 'custom', label: 'Custom Range' }
  ];

  const roleOptions = [
    { value: 'all', label: 'All Roles' },
    { value: 'employer', label: 'Employer View' },
    { value: 'institution', label: 'Institution View' },
    { value: 'admin', label: 'Admin View' }
  ];

  const overviewStats = [
    {
      title: 'Total Placements',
      value: '1,248',
      change: '+18.2%',
      changeType: 'positive',
      icon: 'Briefcase',
      iconColor: 'var(--color-success)',
      trend: 'up'
    },
    {
      title: 'Hiring Success Rate',
      value: '87.5%',
      change: '+5.3%',
      changeType: 'positive',
      icon: 'TrendingUp',
      iconColor: 'var(--color-primary)',
      trend: 'up'
    },
    {
      title: 'Active Employers',
      value: '342',
      change: '+24',
      changeType: 'positive',
      icon: 'Building2',
      iconColor: 'var(--color-accent)',
      trend: 'up'
    },
    {
      title: 'Avg. Time to Hire',
      value: '18 days',
      change: '-3 days',
      changeType: 'positive',
      icon: 'Clock',
      iconColor: 'var(--color-warning)',
      trend: 'down'
    }
  ];

  const tabs = [
    { id: 'overview', label: 'Overview', icon: 'LayoutDashboard' },
    { id: 'hiring-trends', label: 'Hiring Trends', icon: 'TrendingUp' },
    { id: 'placement-stats', label: 'Placement Statistics', icon: 'BarChart3' },
    { id: 'employer-performance', label: 'Employer Performance', icon: 'Award' },
    { id: 'custom-reports', label: 'Custom Reports', icon: 'FileText' }
  ];

  const handleExportDashboard = () => {
    const dashboardData = {
      dateRange,
      role: selectedRole,
      stats: overviewStats,
      exportedAt: new Date()?.toISOString()
    };
    
    const dataStr = JSON.stringify(dashboardData, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `analytics-dashboard-${Date.now()}.json`;
    link?.click();
    URL.revokeObjectURL(url);
  };

  const handleShareDashboard = () => {
    const shareUrl = `${window.location?.origin}/analytics-reporting-dashboard?range=${dateRange}&role=${selectedRole}`;
    navigator.clipboard?.writeText(shareUrl);
    alert('Dashboard link copied to clipboard!');
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case 'overview':
        return (
          <div className="space-y-6">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {overviewStats?.map((stat, index) => (
                <StatCard key={index} {...stat} />
              ))}
            </div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
                <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Recent Activity</h3>
                <div className="space-y-3">
                  {[
                    { action: 'New placement recorded', company: 'Tech Innovations', time: '2 hours ago', icon: 'CheckCircle', color: 'var(--color-success)' },
                    { action: 'Employer registered', company: 'DataTech Solutions', time: '5 hours ago', icon: 'UserPlus', color: 'var(--color-primary)' },
                    { action: 'Report generated', company: 'Monthly Analytics', time: '1 day ago', icon: 'FileText', color: 'var(--color-accent)' },
                    { action: 'Student profile updated', company: '45 profiles', time: '2 days ago', icon: 'Users', color: 'var(--color-warning)' }
                  ]?.map((activity, idx) => (
                    <div key={idx} className="flex items-start gap-3 p-3 rounded-lg hover:bg-muted/50 transition-smooth">
                      <div 
                        className="w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0"
                        style={{ backgroundColor: `${activity?.color}15` }}
                      >
                        <Icon name={activity?.icon} size={16} color={activity?.color} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-card-foreground">{activity?.action}</p>
                        <p className="text-xs text-muted-foreground">{activity?.company}</p>
                      </div>
                      <span className="text-xs text-muted-foreground whitespace-nowrap">{activity?.time}</span>
                    </div>
                  ))}
                </div>
              </div>

              <div className="bg-card rounded-xl p-6 border border-border shadow-warm-sm">
                <h3 className="text-lg font-heading font-semibold text-card-foreground mb-4">Quick Insights</h3>
                <div className="space-y-4">
                  <div className="p-4 rounded-lg bg-success/10 border border-success/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="TrendingUp" size={18} color="var(--color-success)" />
                      <h4 className="text-sm font-semibold text-success">Top Performing Sector</h4>
                    </div>
                    <p className="text-sm text-card-foreground">Information Technology with 342 placements</p>
                  </div>
                  <div className="p-4 rounded-lg bg-primary/10 border border-primary/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Award" size={18} color="var(--color-primary)" />
                      <h4 className="text-sm font-semibold text-primary">Best Employer</h4>
                    </div>
                    <p className="text-sm text-card-foreground">Tech Innovations with 95% satisfaction rate</p>
                  </div>
                  <div className="p-4 rounded-lg bg-accent/10 border border-accent/20">
                    <div className="flex items-center gap-2 mb-2">
                      <Icon name="Users" size={18} color="var(--color-accent)" />
                      <h4 className="text-sm font-semibold text-accent">Student Success</h4>
                    </div>
                    <p className="text-sm text-card-foreground">87.5% placement rate across all programs</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'hiring-trends':
        return <HiringTrendsTab dateRange={dateRange} />;
      case 'placement-stats':
        return <PlacementStatisticsTab dateRange={dateRange} />;
      case 'employer-performance':
        return <EmployerPerformanceTab dateRange={dateRange} />;
      case 'custom-reports':
        return <CustomReportsTab />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-7xl mx-auto p-4 md:p-6 lg:p-8">
        <div className="mb-6">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-6">
            <div>
              <h1 className="text-2xl md:text-3xl font-heading font-bold text-foreground mb-2">
                Analytics & Reporting Dashboard
              </h1>
              <p className="text-muted-foreground">
                Comprehensive insights into platform performance and hiring metrics
              </p>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button variant="outline" iconName="Share2" onClick={handleShareDashboard}>
                Share Dashboard
              </Button>
              <Button variant="default" iconName="Download" onClick={handleExportDashboard}>
                Export Data
              </Button>
            </div>
          </div>

          <div className="flex flex-col md:flex-row gap-4 mb-6">
            <Select
              options={dateRangeOptions}
              value={dateRange}
              onChange={setDateRange}
              className="w-full md:w-64"
              label="Date Range"
            />
            <Select
              options={roleOptions}
              value={selectedRole}
              onChange={setSelectedRole}
              className="w-full md:w-64"
              label="View As"
            />
          </div>

          <div className="border-b border-border overflow-x-auto">
            <div className="flex gap-1 min-w-max">
              {tabs?.map((tab) => (
                <button
                  key={tab?.id}
                  onClick={() => setActiveTab(tab?.id)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium transition-smooth border-b-2 whitespace-nowrap ${
                    activeTab === tab?.id
                      ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={16} />
                  {tab?.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="animate-fadeIn">
          {renderTabContent()}
        </div>
      </div>
    </div>
  );
};

export default AnalyticsReportingDashboard;